import React, { JSX, useMemo } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  EN_CENSUS_FEATURE_ENABLED,
  EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED,
} from "@platform/feature-flags";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Divider as MuiDivider,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Button as MuiButton,
  Stack,
  Tooltip,
  useTheme,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ArrowBack } from "@mui/icons-material";
import { Button, TextField, Typography } from "@platform/design-system";
import { useLocation, useNavigate } from "react-router";
import {
  CLIENT_ACTION_VALUES,
  RFP_ACTION_VALUES,
  TECH_CREDIT_TYPES,
} from "@platform/rfp-core";

import InputDate from "../../../components/inputDate";
import { CustomAlert } from "../../../components";
import { ROUTE_NAMES } from "../../../constants";
import { submitEvent } from "../../../utils/tracking";
import { scrollToTop } from "../../../utils";
import Title from "../components/title";
import {
  CLIENT_TYPES,
  DUE_DATE_ALERT_STATUS,
  EN_CENSUS_AVAILABLE_VALUES,
  EN_PUSH_SOLD_PLAN_DATA_VALUES,
  REQUESTED_SYSTEMS,
  RFP_ACTION_TYPES,
} from "../constants";
import { useRfpContext } from "../contexts";
import { validateDueDateWhitinDays } from "../utils";

const { UPDATE_BACK_STEP, UPDATE_FIELD, UPDATE_NEXT_STEP } = RFP_ACTION_TYPES;
const { ACTIVE_STEP } = RFP_ACTION_VALUES;

const {
  CLIENT_ID,
  DUE_DATE,
  EFFECTIVE_DATE,
  EN_CENSUS_AVAILABLE,
  EN_PUSH_SOLD_PLAN_DATA,
  IS_RFP_FORM_VALID,
  MARKETING_OBJECTIVES,
  REQUESTED_SYSTEM,
  HAS_BEN_ADMIN_SYSTEM_STATUS,
  REQUESTED_SYSTEM_OTHER,
  TECH_CREDIT_AMOUNT,
  TECH_CREDIT_TYPE,
  TECH_IMPLEMENTATION,
} = CLIENT_ACTION_VALUES;

const { WARNING } = DUE_DATE_ALERT_STATUS;

const NO_VALUE = "0";
const YES_VALUE = "1";
const UNKNOWN_VALUE = "unknown";
const EMPLOYEE_NAVIGATOR_VALUE = "employeeNavigator";
const CENSUS_AVAILABLE_YES = EN_CENSUS_AVAILABLE_VALUES.YES;
const CENSUS_AVAILABLE_NO = EN_CENSUS_AVAILABLE_VALUES.NO;
const PUSH_SOLD_PLAN_DATA_YES = EN_PUSH_SOLD_PLAN_DATA_VALUES.YES;
const PUSH_SOLD_PLAN_DATA_NO = EN_PUSH_SOLD_PLAN_DATA_VALUES.NO;

interface RfpFormLocationState {
  prevPath?: string;
}

function Divider(): JSX.Element {
  const theme = useTheme();
  return <MuiDivider sx={{ margin: theme.spacing(4, 0) }} />;
}

// eslint-disable-next-line complexity -- Legacy form page with many conditional sections; full decomposition is out of scope.
const RfpForm = ({ px }: { px: number }): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { form, clientErrors, rfp, setRfp, isSavingDraftRfp } = useRfpContext();
  const enableEnCensus = useFeatureIsOn(EN_CENSUS_FEATURE_ENABLED);
  const enablePushSoldPlanData = useFeatureIsOn(
    EN_PUSH_SOLD_PLAN_DATA_FEATURE_ENABLED,
  );

  const rfpValues = rfp as Record<string, unknown>;
  const locationState = (location.state as RfpFormLocationState | null) ?? null;

  const getFormString = (key: string): string => {
    const value = form[key as keyof typeof form];
    return typeof value === "string" ? value : "";
  };

  const getError = (key: string): string | undefined => {
    const value = clientErrors[key];
    return typeof value === "string" ? value : undefined;
  };

  const getFormTextValue = (key: string): string | number => {
    const value = form[key as keyof typeof form];
    if (typeof value === "string" || typeof value === "number") {
      return value;
    }
    return "";
  };

  const hasBenAdminSystemStatus = getFormString(HAS_BEN_ADMIN_SYSTEM_STATUS);
  const hasBenAdminSystemStatusDisplayValue =
    hasBenAdminSystemStatus || YES_VALUE;
  const enCensusAvailable = getFormString(EN_CENSUS_AVAILABLE);
  const enCensusError = getError(EN_CENSUS_AVAILABLE);
  const enCensusDisplayValue = enCensusAvailable;
  const enPushSoldPlanData = getFormString(EN_PUSH_SOLD_PLAN_DATA);
  const enPushSoldPlanDataError = getError(EN_PUSH_SOLD_PLAN_DATA);
  const enPushSoldPlanDataDisplayValue = enPushSoldPlanData;
  const isCurrentClient =
    form.isCurrentClient === true ||
    form.clientType === CLIENT_TYPES.CURRENT_CLIENT;
  const enCensusForProspects = form.enCensusForProspects === true;
  const isEmployeeNavigatorRequested =
    getFormString(REQUESTED_SYSTEM) === EMPLOYEE_NAVIGATOR_VALUE;
  const shouldShowEnCensusQuestion =
    enableEnCensus &&
    (isCurrentClient || enCensusForProspects) &&
    isEmployeeNavigatorRequested;
  const shouldShowPushSoldPlanDataQuestion =
    enablePushSoldPlanData &&
    (isCurrentClient || enCensusForProspects) &&
    isEmployeeNavigatorRequested;

  const dueDate = getFormString(DUE_DATE);
  const effectiveDate = getFormString(EFFECTIVE_DATE);
  const prevPath = locationState?.prevPath ?? null;
  const activeStep = (rfpValues[ACTIVE_STEP] as number | undefined) ?? 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value, name } = e.target;
    setRfp({ type: UPDATE_FIELD, name, value });
  };

  const handleBenAdminChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { value, name } = e.target;
    setRfp({ type: UPDATE_FIELD, name, value });
    if (value === NO_VALUE || value === UNKNOWN_VALUE) {
      setRfp({ type: UPDATE_FIELD, name: REQUESTED_SYSTEM, value: "" });
      setRfp({ type: UPDATE_FIELD, name: REQUESTED_SYSTEM_OTHER, value: "" });
      setRfp({ type: UPDATE_FIELD, name: EN_CENSUS_AVAILABLE, value: "" });
      setRfp({ type: UPDATE_FIELD, name: EN_PUSH_SOLD_PLAN_DATA, value: "" });
    }
  };

  const handleBenAdminSystemChange = (e: SelectChangeEvent): void => {
    const { value, name } = e.target;
    setRfp({ type: UPDATE_FIELD, name, value });
    if (value !== "other") {
      setRfp({ type: UPDATE_FIELD, name: REQUESTED_SYSTEM_OTHER, value: "" });
    }
  };

  const handleTechImplementationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { value, name } = e.target;
    setRfp({ type: UPDATE_FIELD, name, value });
    if (value === NO_VALUE) {
      setRfp({ type: UPDATE_FIELD, name: TECH_CREDIT_TYPE, value: "" });
      setRfp({ type: UPDATE_FIELD, name: TECH_CREDIT_AMOUNT, value: 0 });
    }
  };

  const handleTechCreditTypeChange = (e: SelectChangeEvent): void => {
    const { value, name } = e.target;
    setRfp({ type: UPDATE_FIELD, name, value });
    if (value === "carrierStandard") {
      setRfp({ type: UPDATE_FIELD, name: TECH_CREDIT_AMOUNT, value: 0 });
    }
  };

  const handleBackNavigation = (): void => {
    navigate(locationState?.prevPath ?? ROUTE_NAMES.RFPS);
  };

  const submitAction = (): void => {
    submitEvent("Next Coverage Clicked");
    if (rfpValues[IS_RFP_FORM_VALID]) {
      setRfp({ type: UPDATE_NEXT_STEP });
    }
    scrollToTop();
  };

  const handleBack = (): void => {
    setRfp({ type: UPDATE_BACK_STEP, value: { handleBackNavigation } });
    scrollToTop();
  };

  const pathsCurrClient = [
    `/${ROUTE_NAMES.CLIENT_SELECTION}`,
    `/${ROUTE_NAMES.CLIENTS}/${getFormString(CLIENT_ID)}`,
  ];

  const getBackBtnProps = (): {
    text: string;
    variant: "text" | "outlined";
    icon: boolean;
  } => {
    let text = "Back";
    let variant: "text" | "outlined" = "text";
    let icon = true;
    if (activeStep === 0 && prevPath === pathsCurrClient[1]) {
      text = "Cancel";
      variant = "outlined";
      icon = false;
    }
    return { text, variant, icon };
  };

  const dueDateAlertType = useMemo<string | null>(() => {
    if (dueDate.length > 0) {
      return validateDueDateWhitinDays(dueDate) as string | null;
    }
    return null;
  }, [dueDate]);

  const dueDateErrorMessage = useMemo(() => {
    if (!dueDate || !effectiveDate) return "";
    const due = new Date(dueDate);
    const eff = new Date(effectiveDate);
    due.setHours(0, 0, 0, 0);
    eff.setHours(0, 0, 0, 0);
    if (due >= eff) {
      return "Please enter a date prior to the effective date.";
    }
    return "";
  }, [dueDate, effectiveDate]);

  const dueDateObj = dueDate ? new Date(dueDate) : null;
  const effectiveDateObj = effectiveDate ? new Date(effectiveDate) : null;
  return (
    <Grid
      container
      display="flex"
      justifyContent="center"
      alignItems="center"
      size={12}
      px={px}
    >
      <Grid size={12}>
        <Title
          title={"RFP Details"}
          subtitle={
            "Provide the following information below to start a RFP for a client."
          }
        />
        <Card sx={{ padding: theme.spacing(1) }}>
          <CardContent>
            <Typography variant="header-large" mb={3}>
              RFP Dates
            </Typography>
            {!!dueDateAlertType && (
              <CustomAlert
                title={
                  dueDateAlertType === WARNING
                    ? "Upcoming Due Date"
                    : "New Due Date Required"
                }
                message={
                  dueDateAlertType === WARNING
                    ? "The provided Due Date is within five business days. Please review before continuing."
                    : "The provided Due Date is now past today’s date. Please enter a new date before continuing."
                }
                mb={4}
                severity={dueDateAlertType}
              />
            )}
            {/* Labels share one grid row and inputs the next, so the inputs stay level when one label wraps */}
            <Box
              sx={{
                maxWidth: "75%",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gridTemplateRows: { md: "auto auto" },
                gridAutoFlow: { xs: "row", md: "column" },
                columnGap: 3,
                rowGap: 0.75,
              }}
            >
              <Typography
                variant="body-large"
                id="marketing-proposal-due-date-label"
                sx={{ alignSelf: "end" }}
              >
                Marketing Presentation Due Date
              </Typography>
              <InputDate
                label=""
                required
                value={dueDateObj}
                textFieldParams={{
                  id: "marketing-proposal-due-date",
                  fullWidth: true,
                  helperText:
                    dueDateErrorMessage != ""
                      ? dueDateErrorMessage
                      : getError(DUE_DATE),
                  error:
                    dueDateErrorMessage != ""
                      ? !!dueDateErrorMessage
                      : !!getError(DUE_DATE),
                  inputProps: {
                    "aria-labelledby": "marketing-proposal-due-date-label",
                  },
                }}
                onChange={(newValue) =>
                  handleChange({
                    target: {
                      name: DUE_DATE,
                      value: newValue?.toISOString() ?? "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                disablePast
              />
              <Typography
                variant="body-large"
                id="coverage-effective-date-label"
                sx={{ alignSelf: "end", mt: { xs: 2.25, md: 0 } }}
              >
                Coverage Effective Date
              </Typography>
              <InputDate
                label=""
                required
                value={effectiveDateObj}
                textFieldParams={{
                  id: "coverage-effective-date",
                  fullWidth: true,
                  helperText: getError(EFFECTIVE_DATE),
                  error: !!getError(EFFECTIVE_DATE),
                  inputProps: {
                    "aria-labelledby": "coverage-effective-date-label",
                  },
                }}
                onChange={(newValue) =>
                  handleChange({
                    target: {
                      name: EFFECTIVE_DATE,
                      value: newValue?.toISOString() ?? "",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                shouldDisableDate={(date) =>
                  dueDate ? new Date(dueDate) >= date : false
                }
                disablePast
              />
            </Box>
            <Divider />
            <Typography variant="header-large" mb={3}>
              Ben Admin Platform
            </Typography>
            <Grid container spacing={3} sx={{ maxWidth: "75%" }}>
              {/* Ben Admin Section */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Stack direction="row" alignItems="flex-start" gap={1} mb={2}>
                  <Typography variant="body-large">
                    Is a Ben Admin Platform currently in place, or will one be
                    implemented for the upcoming enrollment?
                  </Typography>
                </Stack>
                <FormControl>
                  <RadioGroup
                    row
                    name={HAS_BEN_ADMIN_SYSTEM_STATUS}
                    value={hasBenAdminSystemStatusDisplayValue}
                    onChange={handleBenAdminChange}
                  >
                    <FormControlLabel
                      value={YES_VALUE}
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value={NO_VALUE}
                      control={<Radio />}
                      label="No"
                    />
                    <FormControlLabel
                      value={UNKNOWN_VALUE}
                      control={<Radio />}
                      label="I don't know"
                    />
                  </RadioGroup>
                </FormControl>
                {hasBenAdminSystemStatusDisplayValue === YES_VALUE && (
                  <Stack direction="column" gap={2} mt={2}>
                    <FormControl fullWidth required>
                      <InputLabel id="requested-system-label">
                        Which system?
                      </InputLabel>
                      <Select
                        id="requested-system"
                        labelId="requested-system-label"
                        name={REQUESTED_SYSTEM}
                        value={getFormString(REQUESTED_SYSTEM)}
                        onChange={handleBenAdminSystemChange}
                        label="Which system?"
                        error={!!getError(REQUESTED_SYSTEM)}
                      >
                        {REQUESTED_SYSTEMS.map((system) => (
                          <MenuItem key={system.value} value={system.value}>
                            {system.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {getError(REQUESTED_SYSTEM) && (
                        <FormHelperText error>
                          {getError(REQUESTED_SYSTEM)}
                        </FormHelperText>
                      )}
                    </FormControl>
                    {shouldShowEnCensusQuestion && (
                      <Stack direction="column" gap={1}>
                        <FormControl required error={!!enCensusError}>
                          <FormLabel id="en-census-available-label">
                            Is the census currently available in Employee
                            Navigator?
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="en-census-available-label"
                            name={EN_CENSUS_AVAILABLE}
                            value={enCensusDisplayValue}
                            onChange={handleChange}
                          >
                            <FormControlLabel
                              value={CENSUS_AVAILABLE_YES}
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value={CENSUS_AVAILABLE_NO}
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                          {enCensusError && (
                            <FormHelperText>{enCensusError}</FormHelperText>
                          )}
                        </FormControl>
                      </Stack>
                    )}
                    {shouldShowPushSoldPlanDataQuestion && (
                      <Stack direction="column" gap={1}>
                        <FormControl required error={!!enPushSoldPlanDataError}>
                          <FormLabel id="en-push-sold-plan-data-label">
                            Do you want Centro to push sold plan data to
                            EmployeeNavigator after sale?
                          </FormLabel>
                          <RadioGroup
                            row
                            aria-labelledby="en-push-sold-plan-data-label"
                            name={EN_PUSH_SOLD_PLAN_DATA}
                            value={enPushSoldPlanDataDisplayValue}
                            onChange={handleChange}
                          >
                            <FormControlLabel
                              value={PUSH_SOLD_PLAN_DATA_YES}
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value={PUSH_SOLD_PLAN_DATA_NO}
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                          {enPushSoldPlanDataError && (
                            <FormHelperText>
                              {enPushSoldPlanDataError}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Stack>
                    )}
                    {getFormString(REQUESTED_SYSTEM) === "other" && (
                      <TextField
                        name={REQUESTED_SYSTEM_OTHER}
                        value={getFormString(REQUESTED_SYSTEM_OTHER)}
                        onChange={handleChange}
                        fullWidth
                        required
                        label="Specify Ben Admin System"
                        inputProps={{ maxLength: 128 }}
                        helperText={
                          getError(REQUESTED_SYSTEM_OTHER) ||
                          `${getFormString(REQUESTED_SYSTEM_OTHER).length || 0}/128 characters`
                        }
                        error={!!getError(REQUESTED_SYSTEM_OTHER)}
                      />
                    )}
                  </Stack>
                )}
              </Grid>
            </Grid>
            <Divider />
            <Typography variant="header-large" mb={3}>
              Additional Information
            </Typography>
            <Grid container spacing={3}>
              {/* Tech Credits Section */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Stack direction="row" alignItems="flex-start" gap={1} mb={2}>
                  <Typography variant="body-large">
                    Are technology, implementation and/or enrollment credits
                    needed?
                  </Typography>
                </Stack>
                <FormControl>
                  <RadioGroup
                    row
                    name={TECH_IMPLEMENTATION}
                    value={getFormString(TECH_IMPLEMENTATION) || NO_VALUE}
                    onChange={handleTechImplementationChange}
                  >
                    <FormControlLabel
                      value={YES_VALUE}
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value={NO_VALUE}
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
                {getFormString(TECH_IMPLEMENTATION) === YES_VALUE && (
                  <Stack direction="column" gap={2} mt={2}>
                    <FormControl fullWidth required>
                      <InputLabel>Requested Credit Type</InputLabel>
                      <Select
                        name={TECH_CREDIT_TYPE}
                        value={getFormString(TECH_CREDIT_TYPE)}
                        onChange={handleTechCreditTypeChange}
                        label="Requested Credit Type"
                        error={!!getError(TECH_CREDIT_TYPE)}
                      >
                        {TECH_CREDIT_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {getError(TECH_CREDIT_TYPE) && (
                        <FormHelperText error>
                          {getError(TECH_CREDIT_TYPE)}
                        </FormHelperText>
                      )}
                    </FormControl>
                    {getFormString(TECH_CREDIT_TYPE) &&
                      getFormString(TECH_CREDIT_TYPE) !== "carrierStandard" && (
                        <TextField
                          name={TECH_CREDIT_AMOUNT}
                          value={getFormTextValue(TECH_CREDIT_AMOUNT)}
                          onChange={handleChange}
                          fullWidth
                          required
                          type="number"
                          label={
                            getFormString(TECH_CREDIT_TYPE) === "percentage"
                              ? "Amount (%)"
                              : getFormString(TECH_CREDIT_TYPE) === "pepm"
                                ? "Amount (PEPM $)"
                                : "Amount ($)"
                          }
                          slotProps={{
                            htmlInput: {
                              inputMode: "numeric",
                              step: "0.01",
                              min: "0",
                              max:
                                getFormString(TECH_CREDIT_TYPE) ===
                                  "percentage" ||
                                getFormString(TECH_CREDIT_TYPE) === "pepm"
                                  ? "100"
                                  : undefined,
                              onWheel: (
                                e: React.WheelEvent<HTMLInputElement>,
                              ) => e.currentTarget.blur(),
                            },
                          }}
                          helperText={getError(TECH_CREDIT_AMOUNT)}
                          error={!!getError(TECH_CREDIT_AMOUNT)}
                        />
                      )}
                  </Stack>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 12 }}>
                <MuiDivider sx={{ mt: 1, mb: 3 }} />
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  gap={1}
                  mb={3}
                  mt={2}
                >
                  <Typography variant="header-large">
                    Marketing Strategy
                  </Typography>
                  <Tooltip title="i.e. service-related concerns, rate check, competitive situations with another broker">
                    <InfoOutlinedIcon fontSize={"inherit"} cursor="pointer" />
                  </Tooltip>
                </Stack>
                <TextField
                  autoComplete="off"
                  multiline
                  rows={3}
                  name={MARKETING_OBJECTIVES}
                  value={getFormString(MARKETING_OBJECTIVES)}
                  onChange={handleChange}
                  fullWidth
                  label="Objectives / Desired results"
                />
              </Grid>
            </Grid>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              mt={2}
            >
              {/* TODO(ds-button): needs manual review — dynamic variant prop (getBackBtnProps().variant) plus color="brandPrimary" cannot be mapped statically */}
              <MuiButton
                color={"brandPrimary"}
                variant={getBackBtnProps().variant}
                onClick={handleBack}
                startIcon={getBackBtnProps().icon ? <ArrowBack /> : null}
              >
                {getBackBtnProps().text}
              </MuiButton>
              <Button
                data-testid="rfp-details-next"
                variant="primary"
                disabled={!rfpValues[IS_RFP_FORM_VALID] || isSavingDraftRfp}
                onClick={submitAction}
              >
                Next: Coverage
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RfpForm;
